import { useNavigateWithSearch } from 'Modulor/hooks';
import { useEffect, useMemo, useRef } from 'react';
import { useAppSelector } from 'store/hooks';
import { isStatusPageModeSelector, needsInitRefetchSelector, paymentFlowSelector } from 'store/slices/global';

import { getMethodListPath } from '../components/Header/utils';

/**
 * Handles browser back button (popstate) redirect after init refetch.
 * When needsInitRefetch is true and we're not on a status page,
 * pressing browser back navigates to the method list page.
 *
 * Extracted from HeaderController — this is NOT header UI logic.
 */
export const usePopstateRefetch = () => {
  const navigate = useNavigateWithSearch();
  const needsInitRefetch = useAppSelector(needsInitRefetchSelector);
  const isStatusPageMode = useAppSelector(isStatusPageModeSelector);
  const flowType = useAppSelector(paymentFlowSelector);
  const memoizedListPath = useMemo(() => getMethodListPath(flowType), [flowType]);

  const flowRef = useRef({
    needsInitRefetch,
    isStatusPageMode,
    memoizedListPath,
  });

  useEffect(() => {
    flowRef.current = {
      needsInitRefetch,
      isStatusPageMode,
      memoizedListPath,
    };
  }, [needsInitRefetch, isStatusPageMode, memoizedListPath]);

  useEffect(() => {
    const handlePopState = () => {
      const {
        needsInitRefetch: refNeedsInitRefetch,
        isStatusPageMode: refIsStatusPageMode,
        memoizedListPath: refMemoizedListPath,
      } = flowRef.current;
      if (refNeedsInitRefetch && !refIsStatusPageMode) {
        navigate(refMemoizedListPath);
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);
};
